import * as React from "react"
import { Toaster } from "react-hot-toast"
import { RouterProvider } from "react-router-dom"
import { router } from "./router"

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from "@apollo/client"
import { KeyArgsFunction } from "@apollo/client/cache/inmemory/policies"
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev"
import { relayStylePagination } from "@apollo/client/utilities"
import { createConsumer } from "@rails/actioncable"
import ActionCableLink from "graphql-ruby-client/subscriptions/ActionCableLink"
import { CurrentUserProvider } from "./auth/CurrentUserContext"
import { getMetaVar } from "./common/getMetaVar"
import { TooltipProvider } from "./shadcn/ui/tooltip"
import { FlashMessageToasts } from "./ui/FlashMessageToasts"
import { initAmplitude } from "./util/amplitude"
import { initSentry } from "./util/sentry"

initSentry()
initAmplitude()

// @ts-expect-error this is a vite-only feature
if (import.meta.env.DEV) {
  // Adds messages only in a dev environment
  loadDevMessages()
  loadErrorMessages()
}

const cable = createConsumer()

const hasSubscriptionOperation = ({ query: { definitions } }: any) => {
  return definitions.some(
    ({ kind, operation }: any) =>
      kind === "OperationDefinition" && operation === "subscription"
  )
}

const csrfToken = getMetaVar("csrf-token")

if (csrfToken == null) {
  console.warn("missing csrf token")
}

const httpLink = new HttpLink({
  credentials: "same-origin",
  headers: {
    "X-CSRF-Token": csrfToken!,
  },
})

const link = ApolloLink.split(
  hasSubscriptionOperation,
  new ActionCableLink({ cable }),
  httpLink
)

const excludePaginationArgs: KeyArgsFunction = (args) => {
  if (!args) return false
  return Object.keys(args).filter((k) => !["first", "after"].includes(k))
}

const apolloClient = new ApolloClient({
  uri: "/graphql",
  link,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          agencies: relayStylePagination(excludePaginationArgs),
          castingCalls: relayStylePagination(excludePaginationArgs),
          influencers: relayStylePagination(excludePaginationArgs),
          users: relayStylePagination(excludePaginationArgs),
        },
      },
      CastingCall: {
        fields: {
          influencers: relayStylePagination(excludePaginationArgs),
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
})

export const App = () => {
  return (
    <React.StrictMode>
      <ApolloProvider client={apolloClient}>
        <CurrentUserProvider>
          <TooltipProvider delayDuration={0} skipDelayDuration={0}>
            <Toaster
              toastOptions={{
                style: {
                  maxWidth: "100%",
                  textAlign: "left",
                },
                success: {
                  style: {
                    color: "#458B47",
                    backgroundColor: "#D3E9D7",
                  },
                },
                error: {
                  style: {
                    color: "#DB5252",
                    backgroundColor: "#F1DFDF",
                  },
                },
              }}
            />
            <FlashMessageToasts />
            <RouterProvider router={router} />
          </TooltipProvider>
        </CurrentUserProvider>
      </ApolloProvider>
    </React.StrictMode>
  )
}
